import { create } from 'zustand';
import { IUser } from '../types/user.type';
import authLocalStorage from '@/features/auth/_shared/utils/auth-localstorage';
import { useShallow } from 'zustand/react/shallow';

export interface IUserPresence {
  profileImage?: string;
  userId: string;
  roleName?: string;
  fullname: string;
  email: string;
  status: boolean;
}

export interface IUserStore {
  /**
   * State
   */
  userPresence: IUserPresence[];
  user: IUser;

  /**
   * Actions
   */
  setDeleteUser: () => void;
  setUser: (user: IUser) => void;
  setUserPresence: (userPresence?: IUserPresence[]) => void;
}

export const userStore = create<IUserStore>((set) => ({
  /**
   * State
   */
  userPresence: [],
  user: authLocalStorage.getUser() as IUser,

  /**
   * Actions
   */
  setDeleteUser: () => {
    authLocalStorage.clearUser();
    set({ user: {} as IUser });
  },

  setUser: (user: IUser) => {
    authLocalStorage.setUser(user);
    set({ user });
  },

  setUserPresence: (userPresence?: IUserPresence[]) => {
    if (userPresence) {
      set({ userPresence });
    }
  },
}));

export const useUser = () =>
  userStore(
    useShallow((state) => ({
      user: state?.user,
      userId: state?.user?.userId,
      fullname: state?.user?.fullname,
      email: state?.user?.email,
      profileImage: state?.user?.profileImage,
      phoneNumber: state?.user?.phoneNumber,
      maxLoad: state?.user?.maxLoad,
      lastActivity: state?.user?.lastActivity,
      role: state?.user?.role,
      teams: state?.user?.teams,
      organization: state?.user?.organization,
      isLoggedAdmin: state?.user?.role?.name === 'admin',
      isLoggedAgent: state?.user?.role?.name === 'agent',
      isLoggedSupervisor: state?.user?.role?.name === 'supervisor',
    }))
  );

export const useUserPresenceStates = () =>
  userStore(
    useShallow((state) => ({
      userPresence: state?.userPresence,
      totalPresence: state?.userPresence?.length || 0,
    }))
  );

export const useUserStates = () =>
  userStore(
    useShallow((state) => ({
      user: state?.user,
      userId: state?.user?.userId,
      fullname: state?.user?.fullname,
      email: state?.user?.email,
      profileImage: state?.user?.profileImage,
      phoneNumber: state?.user?.phoneNumber,
      maxLoad: state?.user?.maxLoad,
      lastActivity: state?.user?.lastActivity,
      role: state?.user?.role,
      teams: state?.user?.teams,
      organization: state?.user?.organization,
      isLoggedAdmin: state?.user?.role?.name === 'admin',
      isLoggedAgent: state?.user?.role?.name === 'agent',
      isLoggedSupervisor: state?.user?.role?.name === 'supervisor',
      // isAgentSignatureActive: state?.user?.isAgentSignatureActive,
      // agentSignature: state?.user?.agentSignature,
    }))
  );

export type TUserStatesStore = ReturnType<typeof useUserStates>;

export const useUserActions = () =>
  userStore(
    useShallow((state) => ({
      setDeleteUser: state.setDeleteUser,
      setUser: state.setUser,
      setUserPresence: state.setUserPresence,
    }))
  );
